/**
 * PROPOSED ROOF GEOMETRY.
 *
 * THE SAFETY BOUNDARY: a proposal is not a Strand, and only Strands are
 * priced. Nothing here is ever read by buildQuote. A proposal earns a Strand
 * by being confirmed, and loses it the moment its geometry changes.
 *
 * This is deliberately a separate collection rather than a `quotable: false`
 * flag on Strand. A flag is a rule somebody has to remember to check on every
 * new code path; a separate collection is a rule the type system keeps. Every
 * failure this layer exists to prevent - a run bridged across vegetation, a
 * run continued past a front corner into a side return - reached a quote
 * because geometry that nobody had agreed to was already sitting in the
 * priced collection.
 *
 * Coordinates are normalized 0..1 against the photo, exactly as Strand's are,
 * so a proposal and the Strand it becomes describe the same line.
 */

import type { Point } from './document'

/** What produced this geometry. Unknown is treated as unsafe, never as manual. */
export type ProposalOrigin =
  | 'manual' // a person drew it in this app
  | 'ai' // a vision model proposed it
  | 'classical' // the local edge tracer proposed it
  | 'recall' // restored from previously approved geometry for this photo
  | 'legacy_unknown' // migrated from a schema 3 document, provenance unrecorded

export type EndKind = 'endpoint' | 'corner' | 'peak'

/**
 * What the person decided about a run that appears to continue past the front
 * corner of the house onto a side elevation.
 *
 * There is no "dismiss". Dismissing a warning is not a decision about the
 * roof, and the whole point of this control is that somebody said what the
 * geometry means. Sol V2 ran 209 px past a front corner and Gemini ran 226 px;
 * both would have arrived here.
 */
export type SideReturnDecision =
  | 'trim' // cut the run back to the front corner
  | 'include' // the side return really is being lit, quote it
  | 'unresolved' // not decided: the run cannot be confirmed

/**
 * What the person decided about a stretch where the mounting edge is hidden.
 *
 * Unresolved contributes zero footage. So does an excluded gap. Only an
 * explicit "the edge continues behind that" puts hidden length into a quote.
 */
export type OcclusionDecision =
  | 'exclude' // there is a gap; quote nothing across it
  | 'continues' // the same edge runs behind the obstruction; quote it
  | 'unresolved' // not decided: contributes zero and blocks confirmation

/**
 * HOW WELL THE MOUNTING EDGE CAN BE SEEN ALONG A STRETCH.
 *
 * The difference between the last two is the whole of the vegetation rule, and
 * it is a promise the SOURCE makes, not something the app can work out later:
 *
 *   occluded          something is in front of the edge, and the edge is
 *                     plainly there on both sides of it. A palm over a fascia.
 *                     The run stays one straight line and confirms normally.
 *
 *   cannot_determine  nothing in the photograph shows whether the edge carries
 *                     on at all. This state blocks confirmation until a person
 *                     rules on it, and it is the only occlusion state that
 *                     does.
 *
 * A source that marks its guesses `occluded` has quietly turned a guess into
 * quoted footage. See docs/PROPOSAL-SOURCE-CONTRACT.md, clause 2.
 */
export type SpanVisibility = 'clear' | 'occluded' | 'cannot_determine'

export type ObstructionKind =
  | 'leaves' | 'branch' | 'trunk' | 'shadow'
  | 'object' | 'glare' | 'blur' | 'other'

export interface EndState {
  kind: EndKind
  /** True means the run stops here and claims nothing beyond it. */
  open: boolean
  /** A shared coordinate, emitted once and referenced by both runs. */
  weldedTo?: string
  /** This end looks like it crosses onto a side elevation. */
  sideReturnWarning: boolean
  /** Only set when sideReturnWarning is true. */
  sideReturnDecision?: SideReturnDecision
  decidedAt?: string
  /**
   * Where a trim would cut, as an index into points. Supplied by whatever
   * raised the warning, so "Trim at front corner" is a real edit rather than
   * a suggestion the person has to execute by hand.
   */
  trimToIndex?: number
  /**
   * The cut has already been made, and the points beyond the front corner are
   * no longer part of this run.
   *
   * Without this the question could never be answered twice: confirming a trim
   * consumes trimToIndex, and any later edit - a nudge, an undo, a change of
   * mind about an occlusion - withdrew the confirmation and then refused to
   * take it back, because the corner it wanted to trim to was gone. The run
   * became permanently unquoteable, which is a safe failure and a useless one.
   */
  trimApplied?: boolean
}

export interface ProposalSpan {
  /** Indices into RoofProposal.points. toIndex is always fromIndex + 1. */
  fromIndex: number
  toIndex: number
  visibility: SpanVisibility
  obstruction?: ObstructionKind
  decision?: OcclusionDecision
  decidedAt?: string
}

export type ProposalStatus = 'proposed' | 'confirmed' | 'rejected'

export interface RoofProposal {
  id: string
  origin: ProposalOrigin
  /** Only for AI origins, so a result can be traced back to its arm. */
  provider?: string
  promptVersion?: string
  schemaHash?: string
  createdAt: string
  /**
   * The number a person sees: "Roof run 3".
   *
   * Assigned once when the run arrives and never reused, so deleting run 1
   * does not renumber the run somebody is looking at. Not part of the
   * geometry, so renaming nothing here can withdraw a confirmation.
   */
  seq?: number

  points: Point[]
  kind: 'eave' | 'rake'
  ends: { start: EndState; end: EndState }
  /** One per stretch between consecutive points. Always points.length - 1. */
  spans: ProposalSpan[]

  /**
   * THE EDIT COUNTER, AND THE PRIMARY AUTHORITY OVER A CONFIRMATION.
   *
   * Incremented by every editing action that touches points, ends or spans -
   * whether or not the numbers came out different, and however small the
   * change was. A confirmation records the revision it was given, and any
   * later revision withdraws it.
   *
   * This exists because a comparison is the wrong instrument here. Compare
   * rounded coordinates and a small enough nudge survives; compare a 64 bit
   * non-cryptographic hash and a collision, however unlikely, silently keeps
   * geometry priced that nobody agreed to. A counter cannot miss an edit,
   * because it is incremented by the edit rather than inferred from the
   * result.
   */
  geometryRev: number

  status: ProposalStatus
  confirmedAt?: string
  /**
   * THE FIELD THAT CARRIES THE SAFETY PROPERTY.
   *
   * Absent until a person confirms. Present only while the confirmation still
   * describes the geometry on screen. Any geometry or topology edit clears it
   * and deletes the Strand it named, in the same update.
   */
  strandId?: string
  /** The value of geometryRev when this run was confirmed. */
  confirmedRev?: number
  /**
   * The EXACT canonical text of the geometry and topology at confirmation.
   *
   * Full precision, no rounding, compared character for character. Together
   * with confirmedRev this is what actually protects quoted geometry: the
   * counter cannot miss an edit, and the exact string cannot be satisfied by
   * a different line that happens to hash the same.
   *
   * Color, name and product are not in it, so recoloring keeps a run
   * confirmed. Nor are timestamps, so re-saving a document does not withdraw
   * anything.
   */
  confirmedGeometry?: string
  /**
   * A short hash of the same text. An IDENTIFIER, for logs, diffing and the
   * correction record - never the thing a pricing decision rests on.
   */
  confirmedFingerprint?: string

  /**
   * WHICH RUN OF THE MODEL PRODUCED THIS, so a later run can replace it.
   *
   * Set only on proposals that arrived from "Generate AI suggestions", and set
   * to that request's id. Replacing suggestions has to mean something exact:
   * "the ones this generation produced that nobody has included yet", never
   * "everything that looks like it came from a model". A run drawn by hand has
   * no value here and can therefore never be swept up by a regeneration, and
   * neither can a suggestion from an earlier generation that someone kept.
   *
   * Not part of the geometry, so it is not in canonicalGeometry and setting it
   * cannot withdraw a confirmation.
   */
  aiGeneration?: string
}

export interface CorrectionCounts {
  runsProposed: number
  runsConfirmedUnchanged: number
  runsEdited: number
  runsRejected: number
  runsDrawnManually: number
  circlesMoved: number
  circlesAdded: number
  circlesRemoved: number
  runsSplit: number
  endsMarkedOpen: number
  occludedSpansConfirmed: number
  occludedSpansExcluded: number
  occludedSpansLeftUnresolved: number
  sideReturnWarnings: number
  sideReturnTrimmed: number
  sideReturnIncluded: number
  confirmationsWithdrawnByEdit: number
}

export interface CorrectionLog {
  consent: 'granted' | 'declined' | 'unasked'
  consentAt?: string
  imageHash?: string
  counts: CorrectionCounts
  /** How far each moved circle went, in bulbs. */
  movementBulbs: number[]
}

export const emptyCounts = (): CorrectionCounts => ({
  runsProposed: 0,
  runsConfirmedUnchanged: 0,
  runsEdited: 0,
  runsRejected: 0,
  runsDrawnManually: 0,
  circlesMoved: 0,
  circlesAdded: 0,
  circlesRemoved: 0,
  runsSplit: 0,
  endsMarkedOpen: 0,
  occludedSpansConfirmed: 0,
  occludedSpansExcluded: 0,
  occludedSpansLeftUnresolved: 0,
  sideReturnWarnings: 0,
  sideReturnTrimmed: 0,
  sideReturnIncluded: 0,
  confirmationsWithdrawnByEdit: 0,
})

export const emptyCorrectionLog = (): CorrectionLog => ({
  consent: 'unasked',
  counts: emptyCounts(),
  movementBulbs: [],
})
